import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar'
import { useNavigate } from '@tanstack/react-router'
import { IconSparkles } from '@tabler/icons-react'
import imgAudio from '@/assets/audio.webp'
import imgPaint from '@/assets/image.webp'
import imgLearning from '@/assets/learning.webp'
import imgNewsletter from '@/assets/newsletter.webp'
import imgChat from '@/assets/strategy.webp'
import imgVideo from '@/assets/video.webp'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Header } from '@/components/layout/header'
import { RainbowButton } from '@/components/magicui/rainbow-button'
import {
  NoPersonaContent,
  PersonaSensitiveContent,
} from '@/components/persona-guard'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'

const dashboardCards = [
  {
    id: 1,
    label: 'Estratégias',
    background: imgChat,
    path: '/chats',
    category: 'Inovação',
    description: 'Explore novas abordagens para estratégia.',
    disabled: false,
  },
  {
    id: 2,
    label: 'Áudios',
    background: imgAudio,
    path: '/audio',
    category: 'Mídia',
    description: 'Ouça e gerencie seus áudios.',
    disabled: true,
  },
  {
    id: 3,
    label: 'Newsletter',
    background: imgNewsletter,
    path: '/newsletter',
    category: 'Comunicação',
    description: 'Gerencie e visualize newsletters.',
    disabled: true,
  },
  {
    id: 4,
    label: 'Videos',
    background: imgVideo,
    path: '/video',
    category: 'Mídia',
    description: 'Acesse e publique vídeos.',
    disabled: true,
  },
  {
    id: 5,
    label: 'Cursos',
    background: imgLearning,
    path: '/cursos',
    category: 'Educação',
    description: 'Crie cursos interativos.',
    disabled: true,
  },
  {
    id: 6,
    label: 'Imagens',
    background: imgPaint,
    path: '/cursos',
    category: 'Mídia',
    description: 'Crie imagens.',
    disabled: true,
  },
]

export default function Dashboard() {
  const navigate = useNavigate()
  return (
    <>
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <div className='flex flex-col min-h-screen p-6'>
        <PersonaSensitiveContent>
          {/* Original Dashboard Content */}
          <div className='mb-8'>
            <div className='flex justify-between items-center mb-6'>
              <div>
                <h1 className='text-3xl font-bold tracking-tight'>
                  Bem vindo(a)
                </h1>
                <p className='text-muted-foreground mt-2'>
                  Pronto para dar vida à sua próxima ideia?
                </p>
              </div>

              <RainbowButton
                className='text-xs sm:text-sm px-2 whitespace-nowrap h-9'
                disabled
              >
                <IconSparkles />
                Novo Projeto
              </RainbowButton>
            </div>

            {/* Stats Overview */}
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4'>
              <Card className='p-6 bg-gradient-to-br from-purple-50 to-blue-50 hover:border-indigo-500 transition-colors group relative opacity-75 cursor-not-allowed'>
                <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                  <span className='text-indigo-600 font-medium bg-indigo-50 px-4 py-2 rounded-full'>
                    Em Breve
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <div>
                    <p className='text-sm text-muted-foreground'>
                      Projetos Ativos
                    </p>
                    <h2 className='text-2xl font-bold'>0</h2>
                  </div>
                  <div className='h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center'>
                    <svg
                      className='w-5 h-5 text-purple-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                      />
                    </svg>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Main Content Grid */}
          <section className='mb-12'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-2xl font-semibold disabled'>
                Central de Conteúdo
              </h2>
              <div className='flex gap-2 hidden sm:flex'>
                <Button variant='outline' disabled>
                  Classificar por Categoria
                </Button>
                <Button variant='outline' disabled>
                  Filtrar
                </Button>
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4'>
              {dashboardCards.map((card) => (
                <Card
                  key={card.id}
                  className={`relative h-48 overflow-hidden group transition-transform hover:shadow-lg ${card.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-1'}`}
                  onClick={() => !card.disabled && navigate({ to: card.path })}
                >
                  <img
                    src={card.background}
                    alt=''
                    className='absolute inset-0 w-full h-full object-cover'
                  />
                  <div className='absolute inset-0 bg-gradient-to-t from-gray-900/80 to-gray-900/20' />
                  <div className='relative h-full p-4 flex flex-col justify-end'>
                    <Badge variant='secondary' className='mb-2 w-fit'>
                      {card.category}
                    </Badge>
                    <h3 className='text-lg font-semibold text-white'>
                      {card.label}
                    </h3>
                    <p className='text-sm text-gray-200'>{card.description}</p>
                  </div>
                  {card.disabled && (
                    <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                      <span className='text-gray-500 font-medium'>
                        Em Breve
                      </span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className='mb-12'>
            <h2 className='text-2xl font-semibold mb-6'>
              Ferramentas de Criação
            </h2>
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4'>
              <Card className='p-6 hover:border-purple-500 transition-colors relative opacity-75 cursor-not-allowed'>
                <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                  <span className='text-purple-600 font-medium bg-purple-50 px-4 py-2 rounded-full'>
                    Em Breve
                  </span>
                </div>
                <div className='flex gap-4 items-start'>
                  <div className='bg-purple-100 p-3 rounded-lg'>
                    <svg
                      className='w-6 h-6 text-purple-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122'
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className='font-medium mb-2'>Design de voz</h3>
                    <p className='text-sm text-gray-600'>
                      Desenhe uma voz totalmente nova a partir de um prompt de
                      texto.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className='p-6 hover:border-indigo-500 transition-colors group relative opacity-75 cursor-not-allowed'>
                <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                  <span className='text-indigo-600 font-medium bg-indigo-50 px-4 py-2 rounded-full'>
                    Em Breve
                  </span>
                </div>
                <div className='flex gap-4 items-start'>
                  <div className='bg-indigo-100 p-3 rounded-lg group-hover:bg-indigo-200 transition-colors'>
                    <svg
                      className='w-6 h-6 text-indigo-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className='font-semibold mb-2 text-gray-900'>
                      Crie Sua Marca
                    </h3>
                    <p className='text-sm text-gray-600 leading-relaxed'>
                      Desenvolva uma identidade de marca única com logotipos
                      personalizados, paletas de cores e diretrizes de estilo.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Add similar cards for other tools */}
            </div>
          </section>

          {/* Recent Activity */}
          <section className='mt-16 mb-16'>
            {' '}
            {/* Added margin top and bottom */}
            <h2 className='text-2xl font-semibold mb-10'>Atividade Recente</h2>
            <div className='mb-16 flex justify-center'>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 2xl:grid-cols-1 gap-4 w-full max-w-[1200px]'>
                {' '}
                {/* Added spacing between cards */}
                <Card className='p-4 relative opacity-75 cursor-not-allowed'>
                  <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                    <span className='text-purple-600 font-medium bg-purple-50 px-4 py-2 rounded-full'>
                      Em Breve
                    </span>
                  </div>
                  <div className='divide-y divide-gray-200'>
                    <div className='py-3 flex items-center gap-3'>
                      <Avatar className='bg-purple-100 w-8 h-8'>
                        <AvatarImage src='/avatars/grandpa.png' alt='Grandpa' />
                        <AvatarFallback>Q</AvatarFallback>
                      </Avatar>
                      <div className='flex-1'>
                        <h3 className='text-sm font-medium'>
                          Grandpa Spuds Updated
                        </h3>
                        <p className='text-xs text-gray-500'>2 horas atrás</p>
                      </div>
                      <Badge variant='outline' className='text-xs'>
                        Inovação
                      </Badge>
                    </div>
                  </div>
                </Card>
                <Card className='p-4 relative opacity-75 cursor-not-allowed'>
                  <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                    <span className='text-purple-600 font-medium bg-purple-50 px-4 py-2 rounded-full'>
                      Em Breve
                    </span>
                  </div>
                  <div className='divide-y divide-gray-200'>
                    <div className='py-3 flex items-center gap-3'>
                      <Avatar className='bg-purple-100 w-8 h-8'>
                        <AvatarImage src='/avatars/grandpa.png' alt='Grandpa' />
                        <AvatarFallback>V</AvatarFallback>
                      </Avatar>
                      <div className='flex-1'>
                        <h3 className='text-sm font-medium'>
                          Grandpa Spuds Updated
                        </h3>
                        <p className='text-xs text-gray-500'>1 hora atrás</p>
                      </div>
                      <Badge variant='outline' className='text-xs'>
                        Mídia
                      </Badge>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </section>
        </PersonaSensitiveContent>

        <NoPersonaContent>
          {/* New Professional Dashboard */}
          <div className='max-w-7xl mx-auto w-full'>
            <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-8'>
              <div className='mb-4 md:mb-0'>
                <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
                  Bem vindo(a)
                </h1>
                <p className='text-muted-foreground mt-2'>
                  Vamos começar a construir sua presença digital
                </p>
              </div>
            </div>

            <div className='mb-12'>
              <div className='mb-12'>
                <div
                  className='relative group cursor-pointer p-8 h-full flex flex-col justify-center 
                bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl
                transition-all duration-300 hover:scale-[1.02] active:scale-100
                shadow-lg hover:shadow-xl overflow-hidden'
                >
                  {/* Gradient Glow Effect */}
                  <div
                    className='absolute inset-0 bg-gradient-to-b from-white/20 to-transparent 
                   opacity-0 group-hover:opacity-30 transition-opacity duration-300'
                  />

                  <div className='max-w-3xl mx-auto text-center relative'>
                    <h2 className='text-2xl font-bold text-white mb-4 drop-shadow-md'>
                      Comece Sua Jornada
                    </h2>
                    <p className='text-purple-100/90 mb-6 text-base leading-relaxed'>
                      Para desbloquear todo o potencial da nossa plataforma,
                      crie sua primeira persona! Precisa de ajuda? Fale com
                      nossa equipe de suporte, estamos prontos para te orientar!
                    </p>

                    {/* Animated CTA Button */}
                    {/*<button
                      className='inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm
                        rounded-xl text-white font-medium hover:bg-white/20
                        transition-all duration-300 border border-white/5
                        hover:border-white/20'
                    >
                      Criar Primeira Persona
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='ml-2 w-4 h-4'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M17 8l4 4m0 0l-4 4m4-4H3'
                        />
                      </svg>
                    </button>*/}
                  </div>

                  {/* Subtle Grid Pattern */}
                  <div
                    className='absolute inset-0 opacity-10 [background-image:linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] 
                   [background-size:24px_24px]'
                  />
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4'>
              {dashboardCards.map((card) => (
                <Card
                  key={card.id}
                  className={`relative h-48 overflow-hidden group transition-transform hover:shadow-lg ${card.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-1'}`}
                  onClick={() => !card.disabled && navigate({ to: card.path })}
                >
                  <img
                    src={card.background}
                    alt=''
                    className='absolute inset-0 w-full h-full object-cover'
                  />
                  <div className='absolute inset-0 bg-gradient-to-t from-gray-900/80 to-gray-900/20' />
                  <div className='relative h-full p-4 flex flex-col justify-end'>
                    <Badge variant='secondary' className='mb-2 w-fit'>
                      {card.category}
                    </Badge>
                    <h3 className='text-lg font-semibold text-white'>
                      {card.label}
                    </h3>
                    <p className='text-sm text-gray-200'>{card.description}</p>
                  </div>
                  {card.disabled && (
                    <div className='absolute inset-0 bg-white/80 flex items-center justify-center'>
                      <span className='text-gray-500 font-medium'>
                        Em Breve
                      </span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </NoPersonaContent>
      </div>
    </>
  )
}
